import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import CloudflareTurnstile from '../common/CloudflareTurnstile';

const LoginForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [tokenDebug, setTokenDebug] = useState('');
  const { login, loading, error, humanVerificationEnabled } = useAuth();
  const navigate = useNavigate();
  const [siteKey, setSiteKey] = useState(process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY || '');

  // Fetch config on mount to get the site key
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_URL + '/config');
        const config = await response.json();
        
        if (config.turnstileSiteKey) {
          setSiteKey(config.turnstileSiteKey);
        }
        
        // Only log config in development
        if (process.env.REACT_APP_NODE_ENV === 'development') {
          fetch(process.env.REACT_APP_API_URL + '/debug/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              message: 'LoginForm fetched config', 
              data: { 
                config,
                envSiteKey: process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY,
                finalSiteKey: config.turnstileSiteKey || process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY
              },
              timestamp: new Date().toISOString()
            })
          });
        }
      } catch (e) {
        // Only log errors (even in production)
        console.error('Error fetching config:', e);
        fetch(process.env.REACT_APP_API_URL + '/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: 'Error fetching config', 
            data: { error: e.message },
            timestamp: new Date().toISOString()
          })
        });
      }
    };
    
    fetchConfig();
  }, []);

  // Only log component mounting in development
  useEffect(() => {
    if (process.env.REACT_APP_NODE_ENV === 'development') {
      try {
        fetch(process.env.REACT_APP_API_URL + '/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: 'LoginForm mounted', 
            data: { humanVerificationEnabled, siteKey },
            timestamp: new Date().toISOString()
          })
        });
      } catch (e) {}
    }
  }, [humanVerificationEnabled, siteKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only log login attempts in development mode
    if (process.env.REACT_APP_NODE_ENV === 'development') {
      try {
        fetch(process.env.REACT_APP_API_URL + '/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: 'Login attempt', 
            data: { 
              username,
              hasToken: !!turnstileToken,
              humanVerificationEnabled
            },
            timestamp: new Date().toISOString()
          })
        });
      } catch (e) {}
    }
    
    // Only proceed if human verification is disabled or we have a token
    if (humanVerificationEnabled && !turnstileToken) {
      setTokenDebug("Human verification required. Please complete the challenge.");
      return;
    }
    
    const success = await login(username, password, turnstileToken);
    
    if (success) {
      navigate('/chat');
    } else {
      // Only log failures (important for security monitoring)
      try {
        fetch(process.env.REACT_APP_API_URL + '/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: 'Login failed', 
            data: { username, error },
            timestamp: new Date().toISOString()
          })
        });
      } catch (e) {}
    }
  };

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
    // Only show token debug info in development environment
    if (process.env.REACT_APP_NODE_ENV === 'development') {
      setTokenDebug(`Token received: ${token.substring(0, 10)}...`);
    } else {
      setTokenDebug("Verification successful");
    }
    
    // Only log to debug endpoint in development or if there's an issue
    if (process.env.REACT_APP_NODE_ENV === 'development') {
      try {
        fetch(process.env.REACT_APP_API_URL + '/debug/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: 'Turnstile token received in LoginForm', 
            data: { tokenPrefix: token.substring(0, 10) },
            timestamp: new Date().toISOString()
          })
        });
      } catch (e) {}
    }
  };

  return (
    <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-md">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">Login to Your Chatbot</h1>
        <p className="mt-2 text-gray-600">Enter your credentials to access your account</p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}
        
        {tokenDebug && (
          <div className="p-3 text-sm text-blue-700 bg-blue-100 rounded-md">
            {tokenDebug}
          </div>
        )}

        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700">
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            required
            className="input mt-1"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            className="input mt-1"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {humanVerificationEnabled && (
          <div className="flex justify-center my-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Human Verification
              </label>
              <CloudflareTurnstile
                siteKey={siteKey}
                onVerify={handleTurnstileVerify}
                theme="auto"
              />
            </div>
          </div>
        )}

        <div>
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={loading}
            disabled={humanVerificationEnabled && !turnstileToken}
          >
            Sign in
          </Button>
        </div>
      </form>

      <div className="text-center mt-4">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;